/**
 * Setting Service
 * API client for settings management (values + admin metadata)
 */

import api from '@/lib/axios'
import { API_ENDPOINTS } from '@/config/api.constants'
import {
  BulkUpdateSettingsInput,
  CreateSettingDefinitionInput,
  GetSettingsParams,
  Setting,
  SettingCategory,
  SettingDefinition,
  SettingScope,
  SettingStats,
  SetSettingInput,
  SettingsByCategory,
  UpdateSettingDefinitionInput,
} from '@/types/platform/setting.types'

class SettingService {
  /**
   * Get maintenance runtime config (only maintenance keys).
   * Callable by any authenticated user (platform, org, branch). Does not expose other platform settings.
   */
  async getMaintenanceRuntime(): Promise<Record<string, unknown>> {
    const response = await api.get(API_ENDPOINTS.PLATFORM.SETTINGS.MAINTENANCE_RUNTIME)
    return (response.data?.data ?? response.data ?? {}) as Record<string, unknown>
  }

  /**
   * Get public branding settings (logo, favicon, brand name).
   * PUBLIC - no auth required. Used on login, register, and onboarding pages.
   */
  async getPublicBranding(): Promise<{
    logoUrl: string | null
    logoDarkUrl: string | null
    faviconUrl: string | null
    brandName: string | null
    platformName: string | null
  }> {
    const response = await api.get(API_ENDPOINTS.PLATFORM.SETTINGS.PUBLIC_BRANDING)
    return response.data?.data ?? response.data ?? {}
  }

  /**
   * Get settings by scope/category
   */
  async getSettings(params: GetSettingsParams = {}): Promise<SettingsByCategory | Setting[]> {
    const response = await api.get(API_ENDPOINTS.PLATFORM.SETTINGS.LIST, {
      params: {
        scope: params.scope?.toLowerCase(),
        scopeId: params.scopeId,
        category: params.category,
      },
    })
    const data = response.data?.data || response.data
    // API returns { settings: Setting[], pagination } — normalize to array so callers get a flat list
    if (data && typeof data === 'object' && Array.isArray(data.settings)) {
      return data.settings
    }
    if (Array.isArray(data)) return data
    return data
  }

  /**
   * Get specific setting by key (with inheritance chain)
   */
  async getSetting(
    key: string,
    params: GetSettingsParams = {}
  ): Promise<Setting & { inheritanceChain?: Setting[] }> {
    const response = await api.get(API_ENDPOINTS.PLATFORM.SETTINGS.GET_BY_KEY(key), {
      params: {
        scope: params.scope?.toLowerCase(),
        scopeId: params.scopeId,
      },
    })
    return response.data?.data || response.data
  }

  /**
   * Update a single setting value
   */
  async setSetting(key: string, input: SetSettingInput): Promise<Setting> {
    const response = await api.post(API_ENDPOINTS.PLATFORM.SETTINGS.SAVE_EDITED, {
      settings: [{ id: key, value: input.value }],
      scope: input.scope?.toLowerCase(),
      scopeId: input.scopeId,
    })
    return response.data?.data || response.data
  }

  /**
   * Bulk update settings for a scope
   */
  async bulkUpdateSettings(input: BulkUpdateSettingsInput): Promise<Setting[]> {
    const response = await api.put(API_ENDPOINTS.PLATFORM.SETTINGS.BULK_UPDATE, {
      ...input,
      scope: input.scope?.toLowerCase(),
    })
    return response.data?.data || response.data
  }

  /**
   * Get categories for the given scope
   */
  async getCategories(params: GetSettingsParams = {}): Promise<SettingCategory[]> {
    const response = await api.get(API_ENDPOINTS.PLATFORM.SETTINGS.CATEGORIES, {
      params: {
        scope: params.scope?.toLowerCase(),
        scopeId: params.scopeId,
      },
    })
    const data = response.data?.data || response.data

    // metadata response expected: { categories: [...] }
    const categories = data?.categories || data || []

    if (Array.isArray(categories) && categories.length > 0) {
      if (typeof categories[0] === 'object' && 'name' in categories[0]) {
        return categories
      }
      return categories.map((cat: string) => ({
        name: cat.charAt(0).toUpperCase() + cat.slice(1),
        key: cat,
        count: 0,
      }))
    }

    return []
  }

  /**
   * Get setting templates/definitions for non-platform scopes
   */
  async getTemplates(scope?: SettingScope): Promise<Setting[]> {
    const response = await api.get(API_ENDPOINTS.PLATFORM.SETTINGS.TEMPLATES(scope?.toLowerCase() || ''), {})
    const data = response.data?.data || response.data

    if (Array.isArray(data)) {
      return data.map((template: any) => ({
        ...template,
        definition: {
          category: template.category,
          type: template.type,
          description: template.description,
          defaultValue: template.value,
          allowedScopes: template.allowedScopes || [],
          validation: template.validation,
        } as SettingDefinition,
      }))
    }

    return []
  }

  /**
   * Get settings statistics for analytics page
   */
  async getStats(): Promise<SettingStats> {
    const response = await api.get(API_ENDPOINTS.PLATFORM.SETTINGS.STATS, {
      params: {
        scope: 'platform',
      },
    })
    return response.data?.data || response.data
  }

  /**
   * Admin: create setting definition (metadata)
   */
  async createSettingDefinition(input: CreateSettingDefinitionInput): Promise<SettingDefinition> {
    const response = await api.post(API_ENDPOINTS.PLATFORM.SETTINGS.CREATE_DEFINITION, input)
    return response.data?.data || response.data
  }

  /**
   * Admin: update setting definition
   */
  async updateSettingDefinition(id: string, input: UpdateSettingDefinitionInput): Promise<SettingDefinition> {
    const response = await api.put(API_ENDPOINTS.PLATFORM.SETTINGS.UPDATE_DEFINITION(id), input)
    return response.data?.data || response.data
  }

  /**
   * Admin: delete setting definition
   */
  async deleteSettingDefinition(id: string): Promise<void> {
    await api.delete(API_ENDPOINTS.PLATFORM.SETTINGS.DELETE_DEFINITION(id))
  }

  /**
   * Admin: toggle setting definition active/inactive
   */
  async toggleSettingStatus(id: string, isActive: boolean): Promise<SettingDefinition> {
    const response = await api.patch(API_ENDPOINTS.PLATFORM.SETTINGS.TOGGLE_STATUS(id), {
      isActive,
    })
    return response.data?.data || response.data
  }

  /**
   * Delete setting definition by ID
   */
  async deleteSetting(id: string): Promise<void> {
    await api.delete(API_ENDPOINTS.PLATFORM.SETTINGS.DELETE_DEFINITION(id))
  }

  /**
   * Get current platform user's settings (platform context only; no org/branch).
   * Uses inheritance: platform scope + user scope.
   */
  async getUserSettings(params: GetSettingsParams & { groupByCategory?: boolean; includeInherited?: boolean } = {}): Promise<Setting[]> {
    const queryParams = new URLSearchParams()
    if (params.category) queryParams.append('category', params.category)
    if (params.groupByCategory !== undefined) queryParams.append('groupByCategory', String(params.groupByCategory))
    if (params.includeInherited === false) queryParams.append('includeInherited', 'false')
    const url = `${API_ENDPOINTS.PLATFORM.SETTINGS.USER_SETTINGS.GET}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    const response = await api.get(url)
    const data = response.data?.data || response.data
    if (Array.isArray(data.settings)) return data.settings
    if (data.groupedByCategory) return Object.values(data.groupedByCategory).flat() as Setting[]
    return []
  }

  /**
   * Update current platform user's settings. Payload: array of { id or key, value }.
   */
  async updateUserSettings(updates: Array<{ id?: string; key?: string; value: any }>): Promise<Setting[]> {
    const settings = updates.map((u) => ({ id: u.id, key: u.key, value: u.value }))
    const response = await api.put(API_ENDPOINTS.PLATFORM.SETTINGS.USER_SETTINGS.UPDATE, { settings })
    return response.data?.data?.settings || response.data?.data || []
  }
}

export const settingService = new SettingService()
export default settingService


